import store from '../store';

// Google Analytics (GA4) transport.
//
// Globals' track* methods used to postMessage into an iframe with id
// 'analytics-frame'. That element was never added to the page (not in
// public/index.html, not in any component), so getElementById always returned
// null and every trackPage/trackEvent/trackError call has been a silent no-op.
//
// gtag is loaded into the document itself instead of into a frame:
//  - no new root asset to serve. The viewer's origin fork routes unknown root
//    paths to the product site, so an /analytics.html would 404 for exactly the
//    share-link visitors we're trying to count (same trap as /pdf.worker.js).
//  - the script tag is async and appended after boot, so it can never block the
//    render the way the typography.com stylesheet did.
// If the request is blocked (ad blocker, offline kiosk) gtag stays undefined
// and every call below is a no-op again - failure mode is silence, not error.

let measurementId = null;
let started = false;

function isRenderRoute() {
    // /render/narrative/... is the Selenium PDF screenshot pass, not a viewer.
    var loc = window.location || {};
    return (loc.pathname || '').indexOf('/render/narrative') > -1 ||
           (loc.hash || '').indexOf('/render/narrative') > -1;
}

function gtag() {
    if (!window.dataLayer) return;
    window.dataLayer.push(arguments);
}

// user_id is the signed-in username; share-link visitors are anonymous and have
// none, so access_type is what separates them in reporting. Test on category,
// NOT on link_token - a signed-in user carries a link_token too (it belongs to
// their -public companion account, not to their session).
function identity() {
    // app separates viewer traffic from the product site, which reports into
    // the same GA property.
    var base = { app: 'viewer' };
    var user = null;
    try { user = store.getState().user; } catch(e) {}
    if (!user) return Object.assign(base, { access_type: 'unknown' });
    if (user.category === 'anon') return Object.assign(base, { access_type: 'link' });
    return Object.assign(base, { access_type: 'user', user_id: user.username });
}

export function initAnalytics(id, opts) {
    if (started) return;
    if (process.env.NODE_ENV === 'development') return;
    if (!id) return;
    if (isRenderRoute()) return;
    // Browser sessions only. The desktop and iPad clients already report through
    // the heartbeat with better fidelity, GA4 can't keep a client id on their
    // file:// origin anyway, and an unattended kiosk doesn't need a recurring
    // call out to Google on a venue network that may well block it.
    if (opts && opts.nativeApp) return;
    if (window.location.protocol !== 'http:' && window.location.protocol !== 'https:') return;

    measurementId = id.indexOf('G-') === 0 ? id : 'G-' + id;
    started = true;

    window.dataLayer = window.dataLayer || [];
    gtag('js', new Date());
    // Page views are sent explicitly from trackPage (the app is a SPA, and the
    // narrative title is more useful than the URL).
    gtag('config', measurementId, { send_page_view: false });

    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId;
    s.onerror = function() { console.warn('Analytics unavailable'); };
    document.head.appendChild(s);
}

export function trackEvent(action, args) {
    if (!started) return;
    gtag('event', action, Object.assign({ send_to: measurementId }, identity(), args || {}));
}

export function trackPage(page) {
    if (!started) return;
    gtag('event', 'page_view', Object.assign({
        send_to: measurementId,
        page_title: page,
        page_location: window.location.href,
        page_path: page,
    }, identity()));
}

export function trackError(desc, fatal) {
    if (!started) return;
    gtag('event', 'exception', Object.assign({
        send_to: measurementId,
        description: desc,
        fatal: !!fatal,
    }, identity()));
}

export default { initAnalytics, trackEvent, trackPage, trackError };
